import { useMemo, useState } from 'react'
import { Bar, BarChart, LabelList, XAxis, YAxis } from 'recharts'
import {
  AlertTriangle,
  Briefcase,
  Coins,
  DollarSign,
  Map as MapIcon,
  MapPin,
  UserCog,
  Users,
  UserCheck,
  type LucideIcon,
} from 'lucide-react'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ChartContainer, ChartTooltip, type ChartConfig } from '@/shared/components/ui/chart'
import { shadesForBars } from '../../lib/reporteColor'
import { formatNumero, monedaPais } from '../../lib/paisMapa'
import { parseFidelizacionError } from '../../hooks/useFidelizacionError'
import { ejeCategoria } from './palette'

/** Símbolo e icono de la moneda del reporte (Perú → "S./" + Coins; resto → "$" + DollarSign). */
function monedaUI(configId: number) {
  const simbolo = monedaPais(configId).simbolo
  return { simbolo, Icon: simbolo === '$' ? DollarSign : Coins }
}
import { representanteMedifarmaPeru } from '../../lib/representantesMedifarmaPeru'

export interface RepresentanteDatum {
  label: string
  registros: number
  ventas: number
}

type Metrica = 'registros' | 'ventas'

interface Props {
  title: string
  data: RepresentanteDatum[]
  colorNombre: string
  isLoading?: boolean
  error?: unknown
  icon?: LucideIcon
  /** Formatea el monto de ventas ($). Los registros siempre se muestran como entero. */
  formatVentas?: (v: number) => string
  /** Items visibles antes de hacer scroll vertical. */
  maxVisible?: number
  /** Id de configuración para el formateo de números según el país del reporte. */
  configId?: number
  /** Muestra el tab de ventas $ (default true). Pasar false cuando no hay datos de ventas disponibles. */
  showVentas?: boolean
}

const BAR_HEIGHT = 44

const formatEntero = (v: number, configId: number) => formatNumero(v, configId)

/** Una línea etiqueta+valor del detalle del representante (Excel RRVV/RRMM). */
function DetalleLinea({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
  if (!value) return null
  return (
    <div className="flex items-center gap-1.5 text-muted-foreground">
      <Icon className="h-3 w-3 shrink-0" />
      {label}: <span className="font-medium text-foreground">{value}</span>
    </div>
  )
}

/**
 * Detalle enriquecido del representante desde el Excel RRVV/RRMM (solo Medifarma
 * Perú, config 7). No renderiza nada si el nombre no matchea el catálogo.
 */
function DetalleRepresentante({ configId, nombre }: { configId: number; nombre: string }) {
  const info = representanteMedifarmaPeru(configId, nombre)?.[0]
  if (!info) return null
  return (
    <div className="mt-1.5 border-t border-border/50 pt-1.5">
      <div className="mb-1 flex items-center gap-1.5">
        <UserCheck className="h-3 w-3 text-muted-foreground" />
        <span className="font-medium">
          {info.tipo === 'RRVV' ? 'Representante de Ventas' : 'Representante Médico'}
        </span>
      </div>
      {info.tipo === 'RRVV' ? (
        <>
          <DetalleLinea icon={Briefcase} label="Portafolio" value={info.portafolio} />
          <DetalleLinea icon={MapPin} label="Ubicación" value={info.ubicacion} />
          <DetalleLinea icon={MapIcon} label="Zona" value={info.zona} />
          <DetalleLinea icon={UserCog} label="Jefe de Zona" value={info.jefeZona} />
        </>
      ) : (
        <>
          <DetalleLinea icon={MapPin} label="Ciudad" value={info.ciudad} />
          <DetalleLinea icon={UserCog} label="Supervisor" value={info.supervisor} />
        </>
      )}
    </div>
  )
}

/** Tooltip que muestra AMBAS métricas del representante (registros y ventas). */
function RepresentanteTooltip({
  active,
  payload,
  formatVentas,
  configId,
}: {
  active?: boolean
  payload?: Array<{ payload: { categoria: string; registros: number; ventas: number } }>
  formatVentas: (v: number) => string
  configId: number
}) {
  if (!active || !payload?.length) return null
  const d = payload[0].payload
  const { Icon: MonedaIcon } = monedaUI(configId)
  return (
    <div className="rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl">
      <div className="mb-1 font-medium">{d.categoria}</div>
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Users className="h-3 w-3" />
        Registros: <span className="font-medium text-foreground">{formatEntero(d.registros, configId)}</span>
      </div>
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <MonedaIcon className="h-3 w-3" />
        Ventas: <span className="font-medium text-foreground">{formatVentas(d.ventas)}</span>
      </div>
      <DetalleRepresentante configId={configId} nombre={d.categoria} />
    </div>
  )
}

export function BarChartRepresentante({
  title,
  data,
  colorNombre,
  isLoading,
  error,
  icon: Icon = UserCheck,
  formatVentas = (v) =>
    `${monedaUI(configId ?? 0).simbolo} ${formatNumero(v, configId ?? 0, { maximumFractionDigits: 0 })}`,
  maxVisible = 8,
  configId,
  showVentas = true,
}: Props) {
  const [metrica, setMetrica] = useState<Metrica>('registros')
  const moneda = monedaUI(configId ?? 0)

  const sorted = useMemo(
    () => [...data].sort((a, b) => b[metrica] - a[metrica]),
    [data, metrica],
  )

  const shades = shadesForBars(colorNombre)

  // Eje de categorías: en el reporte 7 los labels se muestran completos (una
  // línea) ensanchando el eje; en el resto se trunca a 40 chars con ancho fijo.
  const eje = useMemo(
    () => ejeCategoria(sorted.map((d) => d.label), configId, { anchoFijo: 190, truncar: 40, fontSize: 9 }),
    [sorted, configId],
  )

  const { chartData, chartConfig } = useMemo(() => {
    const cfg: ChartConfig = { valor: { label: 'Total' } }
    const rows = sorted.map((item, index) => {
      const key = `cat-${index}`
      const ratio = sorted.length <= 1 ? 0 : index / (sorted.length - 1)
      const colorIdx = Math.round(ratio * (shades.length - 1))
      cfg[key] = { label: item.label, color: shades[colorIdx] }
      return {
        categoria: item.label,
        registros: item.registros,
        ventas: item.ventas,
        displayName: eje.formatLabel(item.label),
        fill: `var(--color-${key})`,
      }
    })
    return { chartData: rows, chartConfig: cfg }
  }, [sorted, shades, eje])

  const header = (
    <CardHeader className="flex flex-row items-center space-y-0 pb-2 gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
        <CardTitle className="text-sm font-medium truncate">{title}</CardTitle>
      </div>
    </CardHeader>
  )

  const footer = showVentas ? (
    <CardFooter className="pt-0 pb-3 px-3 mt-auto">
      <Tabs value={metrica} onValueChange={(v) => setMetrica(v as Metrica)} className="w-full">
        <TabsList className="w-full h-8">
          <TabsTrigger value="registros" className="flex-1 h-7 px-2 text-xs gap-1">
            <Users className="h-3 w-3" />
            Registros
          </TabsTrigger>
          <TabsTrigger value="ventas" className="flex-1 h-7 px-2 text-xs gap-1">
            <moneda.Icon className="h-3 w-3" />
            Ventas {moneda.simbolo}
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </CardFooter>
  ) : null

  if (isLoading) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700 flex flex-col">
        {header}
        <CardContent className="flex-1">
          <Skeleton className="h-[250px] w-full" />
        </CardContent>
        {footer}
      </Card>
    )
  }

  if (error) {
    const parsed = parseFidelizacionError(error)
    return (
      <Card className="border border-gray-300 dark:border-gray-700 flex flex-col">
        {header}
        <CardContent className="flex-1">
          <div className="flex flex-col items-center justify-center gap-2 h-[250px] text-center px-4">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            <p className="text-sm font-medium">{parsed.title}</p>
            <p className="text-xs text-muted-foreground">{parsed.message}</p>
          </div>
        </CardContent>
        {footer}
      </Card>
    )
  }

  if (chartData.length === 0) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700 flex flex-col">
        {header}
        <CardContent className="flex-1">
          <div className="flex items-center justify-center h-[250px] text-muted-foreground text-sm">
            Sin datos disponibles
          </div>
        </CardContent>
        {footer}
      </Card>
    )
  }

  const containerHeight = maxVisible * BAR_HEIGHT + 20
  const chartHeight = chartData.length * BAR_HEIGHT + 20
  const formatActivo = metrica === 'ventas' ? formatVentas : (v: number) => formatEntero(v, configId ?? 0)

  return (
    <Card className="border border-gray-300 dark:border-gray-700 flex flex-col">
      {header}
      <CardContent className="flex-1">
        <div className="overflow-y-auto" style={{ maxHeight: `${containerHeight}px` }}>
          <ChartContainer
            config={chartConfig}
            style={{ height: `${chartHeight}px` }}
            className="w-full"
          >
            <BarChart
              accessibilityLayer
              data={chartData}
              layout="vertical"
              margin={{ left: 0, right: 72 }}
            >
              <YAxis
                dataKey="displayName"
                type="category"
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                width={eje.width}
                tick={{ fontSize: 9 }}
                interval={0}
              />
              <XAxis dataKey={metrica} type="number" hide />
              <ChartTooltip
                cursor={false}
                content={<RepresentanteTooltip formatVentas={formatVentas} configId={configId ?? 0} />}
              />
              <Bar dataKey={metrica} layout="vertical" radius={5}>
                <LabelList
                  dataKey={metrica}
                  position="right"
                  className="fill-foreground"
                  fontSize={12}
                  formatter={(value: number) => formatActivo(value)}
                />
              </Bar>
            </BarChart>
          </ChartContainer>
        </div>
      </CardContent>
      {footer}
    </Card>
  )
}
